import type { ThemeConfig } from 'antd';

import colors from './colors';

/** https://ant.design/theme-editor */
const theme: ThemeConfig = {
  components: {
    Addon: {
      // antd 6 routes every Input addon through Space.Addon, which paints its background with
      // `colorBgContainerDisabled` whether or not the field is disabled (space/style/addon.js:75).
      // The app's disabled grey is far too dark for a live suffix such as the "km" unit.
      colorBgContainerDisabled: colors['@body-bg'],
      colorBorder: colors['@border-color-split'],
    },
    Alert: {
      borderRadiusLG: 4,
      // @ts-expect-error antd types `lineHeight` as a unitless ratio (number); this token is a CSS length.
      lineHeight: '20px',
      defaultPadding: '8px 12px !important',
      withDescriptionPadding: '16px 12px !important',
      colorSuccessBg: '#F6FFED',
      colorSuccessBorder: '#B7EB8F',

      colorInfoBg: colors['@notification-bg'],
      colorInfoBorder: '#ABD0EF',

      colorWarningBg: '#FFF6C6',
      colorWarningBorder: colors['@warning-color'],

      colorErrorBg: colors['@error-color-background'],
      colorErrorBorder: colors['@error-color-disabled'],
      colorInfo: colors['@primary-color'],
      colorSuccess: '#52c41a',
      colorText: colors['@text-color'],
      colorTextHeading: colors['@text-color'],
      fontSizeIcon: 14,
      withDescriptionIconSize: 24,
    },
    Avatar: {
      colorTextLightSolid: colors['@text-color'],
      colorTextPlaceholder: '#EEF0F4',
    },
    Badge: {
      textFontSizeSM: 9,
      indicatorHeightSM: 15,
    },
    Breadcrumb: {
      fontSize: 12,
      itemColor: colors['@text-color-secondary'],
      linkColor: colors['@text-color-secondary'],
      linkHoverColor: colors['@text-color'],
      lastItemColor: colors['@text-color'],
    },
    Button: {
      iconGap: 12,
      borderRadius: 2,
      fontWeight: 500,
      onlyIconSize: 20,
      contentFontSizeSM: 12,
      defaultShadow: 'none',
      // Preset-colored buttons read their own `<color>ShadowColor` (button/style/variant.js:226); `defaultShadow` does not cover them.
      // @ts-expect-error `greenShadowColor` is on ButtonToken, not ComponentToken, but the components override merges after prepareComponentToken.
      greenShadowColor: 'none',
      primaryShadow: 'none',
      defaultActiveBg: 'transparent',
      colorText: colors['@btn-default-color'],
      colorBorder: colors['@btn-default-border'],
      colorErrorHover: '#801B2F',
      defaultBorderColor: colors['@border-color-split'],
      colorError: colors['@error-color'],
      // Figma "Button": the disabled trio (text, border, fill) is one greyscale step, not antd's
      // translucent black. `colorTextDisabled` is read by a single variant-agnostic block
      // (button/style/variant.js:248), so the primary variant keeps its own colour in components/button.ts.
      colorTextDisabled: colors['@border-color-split'],
      borderColorDisabled: colors['@border-color-split'],
      colorBgContainerDisabled: colors['@body-bg'],
      // Figma "Medium/Padding X". antd derives 15 from `paddingContentHorizontal - lineWidth`;
      // setting `paddingInline` leaves the SM and LG steps on their own tokens.
      paddingInline: 12,
      colorPrimaryHover: colors['@primary-color-dark'],
    },
    Checkbox: {
      // Figma's box is 18; antd would derive 16 from `controlHeight / 2`. Also scales the checkmark.
      controlInteractiveSize: 18,
      // Carried over from before the audit. Read twice by the generator: the box border
      // (checkbox/style/index.js:63) and the checkmark offset (:73-74).
      lineWidth: 2,
      colorBorder: colors['@border-color-split'],
      colorBgContainerDisabled: colors['@background-overlay'],
      // antd spends this on the disabled label too, which Checkbox.tsx corrects through its semantic slot.
      colorTextDisabled: colors['@text-color-secondary'],
      // Figma leaves a checked box unchanged on hover; antd lightens it by default.
      colorPrimaryHover: colors['@primary-color'],
      // @ts-expect-error antd types `lineHeight` as a unitless ratio (number); this token is a CSS length.
      lineHeight: '16px',
    },
    Collapse: {
      colorBorder: colors['@border-color-base'],
      contentPadding: '12px 14px 12px',
    },
    DatePicker: {
      colorBgContainerDisabled: '#F7F8FA',
      colorText: colors['@text-color-secondary'],
      colorIcon: colors['@text-color-secondary'],
      colorTextDisabled: colors['@text-color-secondary'],
    },
    Drawer: {
      // Header block padding. `padding` is read nowhere else in drawer/style/index.js.
      padding: 24,
      // The panel's horizontal inset. Reaches the body (all four sides) and the header's inline
      // padding, which `components/drawer.tsx` zeroes so the separator can be inset by a margin.
      // Emits nothing today -- antd derives `paddingLG` from `sizeLG`, which is also 24 -- but the
      // 24 here is Figma's `spacing/6`, so the drawer should not follow antd's scale if it moves.
      paddingLG: 24,
      // Read only by the close button's box, `calc(fontSizeLG + paddingXS)`. At fontSizeLG 20 this
      // keeps Figma's 24px square.
      paddingXS: 4,
      // Title and header: Figma's `Text/Medium/XL` (20 / 28 / 500).
      fontSizeLG: 20,
      lineHeightLG: 1.4,
      fontWeightStrong: 500,
      // Header border-bottom and footer border-top; Figma draws both in `border/low`.
      colorSplit: colors['@border-color-grey'],
      footerPaddingBlock: 24,
      // 0 rather than 24: the footer takes its inset from the same margin as the header, so its
      // border stops at the content width instead of running edge to edge.
      footerPaddingInline: 0,
      // Every drawer in the app is right-placed, so only this direction is specified in Figma.
      // @ts-expect-error `boxShadowDrawerRight` is built in `theme/util/alias.js:158` but absent from
      // the `AliasToken` interface. Verified by rendering: the declaration reaches the emitted CSS.
      boxShadowDrawerRight: '-2px 0 10px rgba(0, 0, 0, 0.25)',
    },
    Dropdown: {
      colorTextDisabled: 'rgba(0,0,0,0.4)',
    },
    Form: {
      labelColor: colors['@label-color'],
    },
    Input: {
      controlHeightSM: 22,
      // Figma "Input/Primary/Default". Scoped rather than global: the alias reaches Input, TextArea,
      // Password, Search and OTP (they share the "Input" style key) and nothing else, so Select and
      // DatePicker keep their own border.
      colorBorder: colors['@border-color-split'],
      colorTextPlaceholder: colors['@font-grey-light'],
      colorBgContainerDisabled: colors['@body-bg'],
    },
    InputNumber: {
      handleBorderColor: colors['@input-border-color'],
      // InputNumber has its own style key, so the Input border above does not reach it -- and a text
      // field beside a numeric one on the same form would show two different greys.
      colorBorder: colors['@border-color-split'],
      colorBgContainerDisabled: colors['@body-bg'],
    },
    Layout: {
      bodyBg: colors['@body-bg'],
      headerPadding: 0,
      headerHeight: 60,
    },
    Menu: {
      itemActiveBg: colors['@menu-item-active-bg'],
      activeBarBorderWidth: 0,
      itemSelectedColor: colors['@text-color-secondary'],
      itemHeight: 55,
      collapsedWidth: 55,
    },
    Modal: {
      boxShadow: '0 5px 10px rgba(45, 45, 45, 0.2)',
      borderRadiusLG: 8,
      titleFontSize: 20,
      // @ts-expect-error `modal/style/index.d.ts` declares these and `index.js` reads them; only `ThemeConfig`
      // has not caught up. Verified by rendering: `--ant-modal-header-padding` is emitted, so the tokens are live.
      headerPadding: '0 0 16px',
      footerPadding: '16px 0 0',
      confirmBtnsMarginTop: 24,
    },
    Notification: {
      // The type-icon colours are deliberately left to the global tokens, which antd reads directly.
      colorInfoBg: colors['@white'],
      colorErrorBg: colors['@white'],
      colorSuccessBg: colors['@white'],
      colorWarningBg: colors['@white'],
    },
    Pagination: {
      // Feeds `--ant-pagination-item-spacing-actual` for the small size, the only size the app
      // renders; antd's own default is 4.
      marginXXS: 2,
    },
    Popover: {
      borderRadiusLG: 4,
    },
    QRCode: {
      paddingSM: 0,
    },
    Radio: {
      // Figma leaves a checked radio unchanged on hover; antd lightens it by default.
      colorPrimaryHover: colors['@primary-color'],
      colorPrimaryActive: colors['@primary-color'],
      buttonColor: colors['@radio-button-color'],
      buttonCheckedBg: 'aliceblue',
      // Pinned: antd derives it from `colorTextDisabled`, which is overridden below for the plain radio's label.
      buttonCheckedColorDisabled: '#000',
      // Figma "File / Appointment / TabBar" (506:28669): the Works button is 73 wide with its 43px
      // label starting 13px in. antd would derive 15 from `padding - lineWidth`.
      buttonPaddingInline: 13,
      // Figma is an 18px circle with a 9px dot; antd would derive 16/6 from `fontSizeLG`.
      radioSize: 18,
      dotSize: 9,
      colorBorder: colors['@border-color-split'],
      colorBgContainerDisabled: colors['@background-overlay'],
      dotColorDisabled: colors['@text-color-secondary'],
      // antd colours the *wrapper*, which the label inherits, so overriding the label alone is not enough.
      colorTextDisabled: colors['@text-color'],
      // @ts-expect-error antd types `lineHeight` as a unitless ratio (number); this token is a CSS length.
      lineHeight: '16px',
    },
    Rate: {
      starSize: 16,
      starBg: '#A1A3A6',
      starColor: colors['@primary-color'],
      marginXS: 4,
    },
    Select: {
      // Figma gives Select the same resting border as Input; they sit side by side on most forms.
      colorBorder: colors['@border-color-split'],
      colorTextDisabled: 'rgba(0,0,0,0.4)',
    },
    Switch: {
      // Figma "Select Controls / Switch": OFF track = Common/Neutral/high, 40×22, no hover effect.
      // colorTextLightSolid is deliberately not set: it colours the checkedChildren icon
      // (switch/style/index.js:184), and with the inverted blueSwitch palette gone a primary-coloured
      // check would sit invisible on the primary track.
      colorTextQuaternary: colors['@radio-button-color'],
      colorTextTertiary: colors['@radio-button-color'],
      trackMinWidth: 40,
    },
    Table: {
      cellFontSize: 13,
      // Figma "Background/overlay" for a sorted or hovered header cell. antd derives both from its own
      // greys, which sat darker than the new headerBg and drew a visible block in the header.
      headerSortActiveBg: colors['@border-color-grey'],
      headerSortHoverBg: colors['@border-color-grey'],
      // Hides the header column separator, which antd draws as the ::before background of every
      // th but the last (table/style/index.js:95-101).
      headerSplitColor: 'transparent',
      // Figma "Table": header text Text/default, header fill Background/raised, rules Border/low.
      headerColor: colors['@text-color'],
      headerBg: colors['@body-bg'],
      borderColor: colors['@border-color-grey'],
      fontWeightStrong: 700,
      // Figma squares the table. This is `tableRadius`, so it reaches the wrapper and the bottom
      // corners too (table/style/radius.js) -- there is no header-only radius token.
      headerBorderRadius: 0,
    },
    Tooltip: {
      borderRadius: 8,
      colorBgSpotlight: colors['@label-color'],
      paddingXS: 10,
    },
    Tabs: {
      // Removes antd's default 16px gap under the nav.
      horizontalMargin: '0',
    },
    Tag: {
      borderRadiusSM: 3,
      fontSizeSM: 14,
      // `defaultBg` also feeds `tagBorderlessBg`, used by the `filled` variant — antd 6's default.
      // Carried over verbatim: Figma's `Tag` frame describes a different component.
      defaultBg: 'rgba(0, 0, 0, 0.06)',
      defaultColor: colors['@text-color-secondary'],
      // The close button is the only reader of these two in tag/style: `colorIcon` for its resting
      // state, `colorTextHeading` on hover. antd paints both grey, which vanishes on the solid
      // status tags, so the cross follows the tag's own text colour instead.
      colorIcon: 'currentColor',
      colorTextHeading: 'currentColor',
    },
    Typography: {
      fontWeightStrong: 400,
      colorTextDisabled: colors['@text-color-disabled'],
    },
    // No product UI renders antd's Segmented; only the /playground page does.
    Segmented: {
      itemSelectedBg: '#F0F8FF',
      itemSelectedColor: colors['@primary-color'],
      trackBg: '#F7F8FA',
      itemHoverBg: 'transparent',
      itemHoverColor: colors['@primary-color'],
      itemColor: colors['@text-color-secondary'],
      trackPadding: 4,
    },
    Timeline: {
      // antd 6 builds Timeline on top of Steps (timeline/Timeline.js:132), so the dot geometry comes
      // from the Steps tokens. develop sized it through `Steps.dotCurrentSize: 20`; scoping it to
      // Timeline restores the 20px dot without resizing a real Steps.
      dotSize: 20,
    },
    Upload: {
      // The dragger fills its own drop area; antd pads it by `padding` (upload/style/dragger.js:20),
      // which is the only place Upload reads that alias.
      padding: 0,
    },
    Steps: {
      colorPrimary: colors['@primary-color'],
      colorTextDisabled: colors['@grey-20'],
      colorText: colors['@text-color'],
      colorTextDescription: colors['@text-color'],
      fontSizeLG: 14,
      lineHeightLG: 1.25,
    },
  },
  token: {
    borderRadius: 2,
    borderRadiusLG: 2,
    borderRadiusSM: 2,
    borderRadiusXS: 2,
    colorBgContainerDisabled: colors['@disabled-bg'],
    colorBorder: colors['@input-border-color'],
    colorError: colors['@error-color'],
    colorInfo: colors['@info-color'],
    colorLink: colors['@link-color'],
    colorLinkActive: colors['@link-active-color'],
    colorLinkHover: colors['@link-hover-color'],
    colorPrimary: colors['@primary-color'],
    colorSuccess: colors['@success-color'],
    colorText: colors['@text-color'],
    colorTextDescription: colors['@text-color-secondary'],
    colorTextDisabled: '#000',
    colorTextHeading: colors['@text-color-secondary'],
    colorTextLabel: colors['@label-color'],
    colorTextSecondary: colors['@text-color-secondary'],
    colorWarning: colors['@warning-color'],
    controlItemBgHover: colors['@item-hover-bg'],

    fontFamily:
      "Noto Sans, Noto Sans JP, NotoSansKR, NotoSansTC, NotoSansHK, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    fontSizeHeading1: 32,
    fontSizeHeading2: 22,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    /* Seed of the green preset palette (green1-10, greenHover/Active), consumed only by Button color="green". */
    green: colors['@success-color'],
    screenXLMax: 1439,
    screenXXL: 1440,
    screenXXLMin: 1440,
  },
};

export default theme;
